import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core';
import type { SimpleChanges } from '@angular/core';
import { QuestionViewModel } from '../shared/models/questionview.model';
import { FileDetail } from '../shared/components/page/page.model';
import * as fs from 'file-saver';
import { IDispute } from '../shared/models/dispute/dispute.model';
import { IDisputeQuestion } from '../shared/models/dispute/disputeQuestion.model';
import { AppConstants } from '../shared/appconstants';
import { BaseHttpService } from '../shared/services/basehttp.service';
import { Constants } from '../shared/constants';
import { StorageService } from '../shared/services/token-storage.service';

@Component({
  selector: 'app-incident-dispute',
  templateUrl: './incident-dispute.component.html',
  styleUrls: ['./incident-dispute.component.scss'],
  standalone: false,
})
export class IncidentDisputeComponent implements OnInit {
  @Input() question: QuestionViewModel;
  @Input() dispute: IDispute;

  @Output() inputChange: EventEmitter<Boolean> =
    new EventEmitter<Boolean>();

  @Output() disputeRaised =
    new EventEmitter<IDisputeQuestion>();

  @Output() fileUploadInProgress:
    EventEmitter<boolean> =
    new EventEmitter<boolean>();

  attachedFiles: FileDetail[] = [];
  comments: string = '';
  isReadOnly: boolean = false;
  currentQuestionDispute:
    IDisputeQuestion | null = null;

  errorMessages: string[] = [];
  appConstants: AppConstants =
    new AppConstants();
  constant: Constants = new Constants();
  errormgvisible: boolean = false;

  constructor(
    private baseHttpService: BaseHttpService,
    private storage: StorageService
  ) {}

  ngOnChanges(
    changes: SimpleChanges
  ): void {
    try {
      if (
        changes['dispute'] &&
        changes['dispute'].currentValue
      ) {
        this.initializeDisputeData();
      }
    } catch (error) {
      this.logError(
        'Dispute change processing failed',
        error
      );
    }
  }

  ngOnInit(): void {
    try {
      this.initializeDisputeData();
      this.checkReadOnlyStatus();
    } catch (error) {
      this.logError(
        'Dispute initialization failed',
        error
      );
    }
  }

  private initializeDisputeData(): void {
    try {
      if (!this.question) {
        return;
      }

      this.currentQuestionDispute =
        this.findCurrentQuestionInDispute();

      if (
        this.currentQuestionDispute
      ) {
        this.comments =
          this.currentQuestionDispute
            .comments || '';

        this.setFilesFromDispute();
      } else {
        this.currentQuestionDispute =
          {} as IDisputeQuestion;

        this.currentQuestionDispute.questionId =
          this.question.questionId;

        this.comments = '';
        this.attachedFiles = [];
      }

      this.emitDisputeData();
    } catch (error) {
      this.logError(
        'Dispute data initialization failed',
        error
      );
    }
  }

  private findCurrentQuestionInDispute():
    IDisputeQuestion | null {
    try {
      if (
        !this.dispute ||
        !this.dispute.disputeQuestions ||
        !this.question
      ) {
        return null;
      }

      return (
        this.dispute.disputeQuestions.find(
          (dq) =>
            dq.questionId ===
            this.question.questionId
        ) || null
      );
    } catch (error) {
      this.logError(
        'Dispute question lookup failed',
        error
      );

      return null;
    }
  }

  private setFilesFromDispute(): void {
    try {
      if (
        this.currentQuestionDispute &&
        this.currentQuestionDispute.files
      ) {
        this.attachedFiles = [];

        const existingFiles =
          JSON.parse(
            this.currentQuestionDispute
              .files
          );

        const listFiles: any[] =
          Array.isArray(existingFiles)
            ? existingFiles
            : [];

        listFiles.forEach((entry) => {
          const fls =
            new FileDetail();

          fls.filePath =
            entry.filePath;
          fls.id = entry.id;
          fls.sequence =
            entry.sequence;
          fls.name = entry.name;

          this.attachedFiles.push(
            fls
          );
        });
      } else {
        this.attachedFiles = [];
      }
    } catch (error) {
      this.logError(
        'Dispute file parsing failed',
        error
      );

      this.attachedFiles = [];
    }
  }

  private checkReadOnlyStatus(): void {
    try {
      this.isReadOnly =
        this.dispute != null &&
        this.dispute.disputeId > 0 &&
        this.dispute.status ===
          'RAISED';
    } catch (error) {
      this.logError(
        'Read only check failed',
        error
      );

      this.isReadOnly = false;
    }
  }

  dataURItoBlob(
    dataURI: string
  ): Blob {
    try {
      if (!dataURI) {
        return new Blob();
      }

      const byteString = atob(
        dataURI.replace(
          /^data:.*;base64,/,
          ''
        )
      );

      const arrayBuffer =
        new ArrayBuffer(
          byteString.length
        );

      const int8Array =
        new Uint8Array(
          arrayBuffer
        );

      for (
        let i = 0;
        i < byteString.length;
        i++
      ) {
        int8Array[i] =
          byteString.charCodeAt(i);
      }

      return new Blob(
        [int8Array],
        {
          type: 'image/png',
        }
      );
    } catch (error) {
      this.logError(
        'File conversion failed',
        error
      );

      return new Blob();
    }
  }

  openBrowseWindow(
    elementId: number
  ): void {
    try {
      const fileInput =
        document.getElementById(
          'uploadfile' +
            elementId
        ) as HTMLInputElement;

      if (fileInput) {
        fileInput.click();
      }
    } catch (error) {
      this.logError(
        'File browser open failed',
        error
      );
    }
  }

  onCommentsChange(
    comments: string
  ): void {
    try {
      this.comments = comments;

      if (
        !this.currentQuestionDispute
      ) {
        this.currentQuestionDispute =
          {} as IDisputeQuestion;

        if (this.question) {
          this.currentQuestionDispute.questionId =
            this.question.questionId;
        }
      }

      this.currentQuestionDispute.comments =
        comments;

      this.emitDisputeData();
      this.inputChange.emit(true);
    } catch (error) {
      this.logError(
        'Comment update failed',
        error
      );
    }
  }

  emitDisputeData(): void {
    try {
      if (
        !this.currentQuestionDispute ||
        !this.question
      ) {
        return;
      }

      this.currentQuestionDispute.questionId =
        this.question.questionId;

      this.disputeRaised.emit(
        this.currentQuestionDispute
      );
    } catch (error) {
      this.logError(
        'Dispute data emit failed',
        error
      );
    }
  }

  onFileChange(
    files: FileList | null
  ): void {
    try {
      if (
        !files ||
        this.isReadOnly
      ) {
        return;
      }

      this.errorMessages = [];

      const validFiles:
        FileDetail[] = [];

      Array.from(files).forEach(
        (file) => {
          const fileDetail =
            new FileDetail();

          fileDetail.file = file;

          fileDetail.size =
            this.formatFileSize(
              file.size
            );

          const isValidSize =
            this.checkFileSize(
              fileDetail
            );

          const isValidExtension =
            this.checkFileExtension(
              fileDetail
            );

          if (
            isValidSize &&
            isValidExtension
          ) {
            validFiles.push(
              fileDetail
            );
          }
        }
      );

      this.attachedFiles.push(
        ...validFiles
      );

      if (
        validFiles.length > 0
      ) {
        this.saveFileResponse();

        this.inputChange.emit(
          true
        );
      }

      if (
        this.errorMessages.length >
        0
      ) {
        this.errormgvisible =
          true;
      }
    } catch (error) {
      this.logError(
        'File selection failed',
        error
      );
    }
  }

  private checkAndEmitUploadStatus():
    void {
    try {
      const anyLoading =
        this.attachedFiles.some(
          (file) =>
            file.isLoading
        );

      this.fileUploadInProgress.emit(
        anyLoading
      );
    } catch (error) {
      this.logError(
        'Upload status update failed',
        error
      );
    }
  }

  private formatFileSize(
    bytes: number
  ): string {
    try {
      const units = [
        'B',
        'KB',
        'MB',
        'GB',
        'TB',
      ];

      const threshold = 1024;

      if (bytes === 0) {
        return '0 B';
      }

      const unitIndex =
        Math.floor(
          Math.log(bytes) /
            Math.log(threshold)
        );

      const size =
        (
          bytes /
          Math.pow(
            threshold,
            unitIndex
          )
        ).toFixed(2);

      return `${size} ${units[unitIndex]}`;
    } catch (error) {
      this.logError(
        'File size formatting failed',
        error
      );

      return '';
    }
  }

  saveFileResponse(): void {
    try {
      if (
        !this.currentQuestionDispute ||
        !this.question
      ) {
        return;
      }

      const previousFiles =
        this.attachedFiles
          .filter(
            (x) =>
              x.filePath &&
              !x.file
          )
          .map(
            (x, idx) => ({
              Id: x.id,
              filePath:
                x.filePath,
              name: x.name,
              sequence:
                x.sequence ||
                idx + 1,
            })
          );

      const resultFilejson:
        any[] = [
          ...previousFiles,
        ];

      const newFiles =
        this.attachedFiles.filter(
          (x) =>
            x.file &&
            !x.filePath &&
            !x.isFinished
        );

      if (
        newFiles.length === 0
      ) {
        this.currentQuestionDispute.files =
          JSON.stringify(
            resultFilejson
          );

        this.emitDisputeData();
        return;
      }

      const agencyId =
        this.storage.get(
          'LoggedInUserAgencyId'
        );

      newFiles.forEach(
        (x, index) => {
          if (
            x.file &&
            this.question[
              'IsValid'
            ]
          ) {
            const path =
              agencyId
                ? `${agencyId}/${this.question.questionId}`
                : `${this.question.questionId}`;

            const formData =
              new FormData();

            formData.append(
              'file',
              x.file,
              x.file.name
            );

            formData.append(
              'questionId',
              this.question.questionId.toString()
            );

            formData.append(
              'FilePath',
              path
            );

            x.isLoading = true;

            this.fileUploadInProgress.emit(
              true
            );

            this.baseHttpService
              .post(
                this.constant
                  .uploadFileUrl,
                formData
              )
              .subscribe({
                next: (response) => {
                  try {
                    if (
                      response &&
                      response.isScanEnabled &&
                      response.fileId
                    ) {
                      const pollInterval =
                        setInterval(
                          () => {
                            this.baseHttpService
                              .post(
                                this
                                  .constant
                                  .getFileStatusUrl,
                                {
                                  fileId:
                                    response.fileId,
                                }
                              )
                              .subscribe({
                                next: (
                                  statusResponse
                                ) => {
                                  try {
                                    if (
                                      statusResponse &&
                                      statusResponse.status !==
                                        null
                                    ) {
                                      clearInterval(
                                        pollInterval
                                      );

                                      x.isLoading =
                                        false;

                                      this.checkAndEmitUploadStatus();

                                      x.isFinished =
                                        true;

                                      if (
                                        statusResponse.isFine
                                      ) {
                                        resultFilejson.push(
                                          {
                                            Id: response.fileId,
                                            filePath:
                                              response.filePath,
                                            name: x.file
                                              ?.name,
                                            sequence:
                                              previousFiles.length +
                                              index +
                                              1,
                                          }
                                        );

                                        if (
                                          this
                                            .currentQuestionDispute
                                        ) {
                                          this.currentQuestionDispute.files =
                                            JSON.stringify(
                                              resultFilejson
                                            );
                                        }

                                        if (
                                          resultFilejson.length ===
                                          this
                                            .attachedFiles
                                            .length
                                        ) {
                                          this.emitDisputeData();
                                        }
                                      } else {
                                        const fileIndex =
                                          this.attachedFiles.indexOf(
                                            x
                                          );

                                        if (
                                          fileIndex >
                                          -1
                                        ) {
                                          this.attachedFiles.splice(
                                            fileIndex,
                                            1
                                          );

                                          this.pushErrorMsg(
                                            `File '${x.file?.name}' is corrupted and has been removed.`
                                          );

                                          this.errormgvisible =
                                            true;

                                          this.emitDisputeData();
                                        }
                                      }
                                    }
                                  } catch (
                                    error
                                  ) {
                                    clearInterval(
                                      pollInterval
                                    );

                                    x.isLoading =
                                      false;

                                    this.checkAndEmitUploadStatus();

                                    this.logError(
                                      'File scan processing failed',
                                      error
                                    );
                                  }
                                },
                                error: (
                                  error
                                ) => {
                                  clearInterval(
                                    pollInterval
                                  );

                                  x.isLoading =
                                    false;

                                  this.checkAndEmitUploadStatus();

                                  x.isFinished =
                                    true;

                                  this.logError(
                                    'File status check failed',
                                    error
                                  );
                                },
                              });
                          },
                          10000
                        );
                    }

                    if (
                      response &&
                      !response.isScanEnabled
                    ) {
                      x.isLoading =
                        false;

                      this.checkAndEmitUploadStatus();

                      x.isFinished =
                        true;

                      resultFilejson.push(
                        {
                          Id: response.fileId,
                          filePath:
                            response.filePath,
                          name: x.file
                            ?.name,
                          sequence:
                            previousFiles.length +
                            index +
                            1,
                        }
                      );

                      if (
                        this
                          .currentQuestionDispute
                      ) {
                        this.currentQuestionDispute.files =
                          JSON.stringify(
                            resultFilejson
                          );
                      }

                      if (
                        resultFilejson.length ===
                        this.attachedFiles
                          .length
                      ) {
                        this.emitDisputeData();
                      }
                    }
                  } catch (error) {
                    x.isLoading =
                      false;

                    this.checkAndEmitUploadStatus();

                    this.logError(
                      'File upload response processing failed',
                      error
                    );
                  }
                },

                error: (error) => {
                  x.isLoading =
                    false;

                  this.checkAndEmitUploadStatus();

                  this.logError(
                    'File upload failed',
                    error
                  );
                },
              });
          }
        }
      );
    } catch (error) {
      this.checkAndEmitUploadStatus();

      this.logError(
        'File upload setup failed',
        error
      );
    }
  }

  deleteFile(
    fileIndex: number
  ): void {
    try {
      if (
        fileIndex > -1 &&
        !this.isReadOnly
      ) {
        this.attachedFiles.splice(
          fileIndex,
          1
        );

        if (
          this.currentQuestionDispute
        ) {
          const remainingFiles =
            this.attachedFiles.map(
              (file, index) => ({
                Id: file.id,
                filePath:
                  file.filePath,
                name:
                  file.name ||
                  file.file?.name,
                sequence:
                  file.sequence ||
                  index + 1,
              })
            );

          this.currentQuestionDispute.files =
            JSON.stringify(
              remainingFiles
            );
        }

        this.emitDisputeData();

        this.inputChange.emit(
          true
        );
      }
    } catch (error) {
      this.logError(
        'File deletion failed',
        error
      );
    }
  }

  downloadFile(
    entry: any
  ): void {
    try {
      if (entry.file) {
        const reader =
          new FileReader();

        reader.readAsDataURL(
          entry.file
        );

        reader.onload = () => {
          try {
            if (reader.result) {
              fs.saveAs(
                reader.result.toString(),
                entry.file['name']
              );
            }
          } catch (error) {
            this.logError(
              'Local file download failed',
              error
            );
          }
        };

        reader.onerror = () => {
          this.logError(
            'Local file read failed',
            reader.error
          );
        };
      } else if (
        entry.filePath
      ) {
        const payLoad = {
          filePath:
            entry.filePath,
          fileName: entry.name,
        };

        this.baseHttpService
          .post(
            this.constant
              .getPreSignedDownloadUrl,
            payLoad
          )
          .subscribe({
            next: (response) => {
              try {
                if (
                  response &&
                  response.preSignedUrl
                ) {
                  window.open(
                    response.preSignedUrl,
                    '_blank',
                    'noopener,noreferrer'
                  );
                }
              } catch (error) {
                this.logError(
                  'File download open failed',
                  error
                );
              }
            },

            error: (error) => {
              this.logError(
                'File download URL generation failed',
                error
              );
            },
          });
      }
    } catch (error) {
      this.logError(
        'File download failed',
        error
      );
    }
  }

  private checkFileSize(
    file: FileDetail
  ): boolean {
    try {
      const allowedFileSizeInMB =
        this.appConstants
          .disputeFileConfig
          .maxFileSizeInMB;

      const allowedFileSizeLimitUnit =
        this.appConstants
          .disputeFileConfig
          .maxFileSizeLimitUnit;

      if (
        allowedFileSizeInMB !=
          null &&
        allowedFileSizeInMB > 0 &&
        allowedFileSizeLimitUnit !=
          null &&
        allowedFileSizeLimitUnit
          .length > 0
      ) {
        const allowedSizeInBytes =
          allowedFileSizeInMB *
          1024 *
          1024;

        if (
          file.file &&
          file.file.size >
            allowedSizeInBytes
        ) {
          this.pushErrorMsg(
            "File '" +
              file.file.name +
              "' size (" +
              this.formatFileSize(
                file.file.size
              ) +
              ') should be less than ' +
              allowedFileSizeInMB +
              ' ' +
              allowedFileSizeLimitUnit
          );

          return false;
        }
      }

      return true;
    } catch (error) {
      this.logError(
        'File size validation failed',
        error
      );

      return false;
    }
  }

  private checkFileExtension(
    file: FileDetail
  ): boolean {
    try {
      const allowedExtensions =
        this.appConstants
          .disputeFileConfig
          .fileExtensions;

      if (
        allowedExtensions !=
          null &&
        allowedExtensions.length >
          0 &&
        file.file
      ) {
        const fileName =
          file.file.name || '';

        const lastDotIndex =
          fileName.lastIndexOf(
            '.'
          );

        const uploadedFileExtension =
          lastDotIndex >= 0
            ? fileName
                .substring(
                  lastDotIndex
                )
                .toLowerCase()
            : '';

        if (
          !allowedExtensions.includes(
            uploadedFileExtension
          )
        ) {
          this.pushErrorMsg(
            "File '" +
              file.file.name +
              "' extension should be one of the following: " +
              allowedExtensions.join(
                ', '
              )
          );

          return false;
        }
      }

      return true;
    } catch (error) {
      this.logError(
        'File extension validation failed',
        error
      );

      return false;
    }
  }

  pushErrorMsg(
    errorMsg: string
  ): void {
    try {
      if (
        !this.errorMessages.includes(
          errorMsg
        )
      ) {
        this.errorMessages.push(
          errorMsg
        );
      }
    } catch (error) {
      this.logError(
        'Error message update failed',
        error
      );
    }
  }

  private logError(
    context: string,
    error: unknown
  ): void {
    try {
      const messages:
        string[] = [];

      let currentError:
        unknown = error;

      let depth = 0;

      while (
        currentError &&
        depth < 5
      ) {
        if (
          currentError instanceof
          Error
        ) {
          messages.push(
            currentError.message ||
              currentError.name
          );

          const nestedError =
            currentError as Error & {
              cause?: unknown;
              innerException?: unknown;
              innerError?: unknown;
            };

          currentError =
            nestedError.cause ??
            nestedError.innerException ??
            nestedError.innerError ??
            null;
        } else if (
          typeof currentError ===
            'object' &&
          currentError !== null &&
          'message' in
            currentError &&
          typeof (
            currentError as {
              message?: unknown;
            }
          ).message ===
            'string'
        ) {
          messages.push(
            (
              currentError as {
                message: string;
              }
            ).message
          );

          currentError = null;
        } else {
          messages.push(
            'Unknown error'
          );

          currentError = null;
        }

        depth++;
      }

      console.error(
        `${context}${
          messages.length
            ? `: ${messages.join(
                ' | '
              )}`
            : ''
        }`
      );
    } catch {
      console.error(
        context
      );
    }
  }
}